// src/lib/retry.js
/**
 * Retry utility for failed API calls
 *
 * Provides exponential backoff retry logic for network requests
 */

import { logWarn, logError } from "./logger";
import { isRetryableError } from "./errorUtils";

/**
 * Default retry configuration
 */
const DEFAULT_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffFactor: 2,
  retryableErrors: null, // Use isRetryableError by default
};

/**
 * Calculate delay for retry attempt
 */
function calculateDelay(attempt, config) {
  const delay = Math.min(
    config.initialDelay * Math.pow(config.backoffFactor, attempt),
    config.maxDelay
  );

  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 0.3 * delay;
  return delay + jitter;
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 *
 * @param {Function} fn - Function to retry (should return a Promise)
 * @param {Object} config - Retry configuration
 * @param {number} config.maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} config.initialDelay - Initial delay in ms (default: 1000)
 * @param {number} config.maxDelay - Maximum delay in ms (default: 10000)
 * @param {number} config.backoffFactor - Backoff multiplier (default: 2)
 * @param {Function} config.retryableErrors - Function to check if error is retryable
 * @param {Function} config.onRetry - Callback called before each retry
 * @returns {Promise} - Result of the function or throws last error
 *
 * @example
 * const result = await retry(
 *   () => fetch('/api/data'),
 *   { maxRetries: 3, initialDelay: 1000 }
 * );
 */
export async function retry(fn, config = {}) {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  let lastError;
  let attempt = 0;

  while (attempt <= finalConfig.maxRetries) {
    try {
      const result = await fn();
      return result;
    } catch (error) {
      lastError = error;

      // Check if error is retryable
      const shouldRetry = finalConfig.retryableErrors
        ? finalConfig.retryableErrors(error)
        : isRetryableError(error);

      // Don't retry if error is not retryable or we've exhausted retries
      if (!shouldRetry || attempt >= finalConfig.maxRetries) {
        if (attempt > 0) {
          logError(`Retry failed after ${attempt} attempt(s)`, error, {
            maxRetries: finalConfig.maxRetries,
            attempt,
          });
        }
        throw error;
      }

      // Calculate delay and wait before retrying
      const delay = calculateDelay(attempt, finalConfig);

      logWarn(`Retrying after ${delay}ms (attempt ${attempt + 1}/${finalConfig.maxRetries})`, {
        error: error.message,
        attempt: attempt + 1,
      });

      // Call onRetry callback if provided
      if (finalConfig.onRetry) {
        finalConfig.onRetry(error, attempt + 1, delay);
      }

      await sleep(delay);
      attempt++;
    }
  }

  // Should never reach here, but just in case
  throw lastError;
}

/**
 * Retry a Firebase callable function
 *
 * @param {Function} callableFunction - Firebase callable function
 * @param {Object} data - Data to pass to the function
 * @param {Object} config - Retry configuration
 * @returns {Promise} - Result of the function call
 *
 * @example
 * const sendEmail = httpsCallable(functions, 'sendEmail');
 * const result = await retryCallable(sendEmail, { to: 'user@example.com' });
 */
export async function retryCallable(callableFunction, data = {}, config = {}) {
  return retry(() => callableFunction(data), {
    ...config,
    retryableErrors: (error) => {
      // Retry on network errors and server errors
      if (
        error.code === "functions/unavailable" ||
        error.code === "functions/deadline-exceeded" ||
        error.code === "functions/internal"
      ) {
        return true;
      }
      return isRetryableError(error);
    },
  });
}

/**
 * Retry a Firestore operation
 *
 * @param {Function} operation - Firestore operation (getDoc, setDoc, etc.)
 * @param {Object} config - Retry configuration
 * @returns {Promise} - Result of the operation
 *
 * @example
 * const docRef = doc(db, 'collection', 'id');
 * const docSnap = await retryFirestore(() => getDoc(docRef));
 */
export async function retryFirestore(operation, config = {}) {
  return retry(operation, {
    ...config,
    retryableErrors: (error) => {
      // Retry on specific Firestore errors
      if (
        error.code === "unavailable" ||
        error.code === "deadline-exceeded" ||
        error.code === "aborted"
      ) {
        return true;
      }
      return isRetryableError(error);
    },
  });
}

/**
 * Retry with custom condition
 *
 * @param {Function} fn - Function to retry
 * @param {Function} shouldRetry - Function that returns true if error should be retried
 * @param {Object} config - Retry configuration
 * @returns {Promise} - Result of the function
 */
export async function retryWithCondition(fn, shouldRetry, config = {}) {
  return retry(fn, {
    ...config,
    retryableErrors: shouldRetry,
  });
}
